// src/config.js
// NOTE: CRA only replaces process.env.REACT_APP_* with static access.
// Dynamic access like process.env[key] does NOT get replaced at build time.

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT || 'local';
const IS_PRODUCTION = ENVIRONMENT === 'production';

const requireProdValue = (value, key) => {
    if (IS_PRODUCTION && (!value || value.trim() === '')) {
        throw new Error(`Missing required environment variable: ${key}`);
    }
    return value;
};

const API_CONFIGS = {
    local: {
        javaBackend: process.env.REACT_APP_JAVA_BACKEND_URL || 'http://localhost:8080',
        userService: process.env.REACT_APP_USER_SERVICE_URL || 'http://localhost:8080/user-service',
        nodeBackend: process.env.REACT_APP_NODE_BACKEND_URL || 'http://localhost:3002',
        wsUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8080',
    },
    dev: {
        javaBackend: process.env.REACT_APP_JAVA_BACKEND_URL || 'http://localhost:8080',
        userService: process.env.REACT_APP_USER_SERVICE_URL || 'http://localhost:8080/user-service',
        nodeBackend: process.env.REACT_APP_NODE_BACKEND_URL || 'http://localhost:3002',
        wsUrl: process.env.REACT_APP_WS_URL || 'ws://localhost:8080',
    },
    qa: {
        javaBackend: requireProdValue(process.env.REACT_APP_JAVA_BACKEND_URL, 'REACT_APP_JAVA_BACKEND_URL') || 'https://placeholder-qa-backend',
        userService: requireProdValue(process.env.REACT_APP_USER_SERVICE_URL, 'REACT_APP_USER_SERVICE_URL') || 'https://placeholder-qa-user-service',
        nodeBackend: requireProdValue(process.env.REACT_APP_NODE_BACKEND_URL, 'REACT_APP_NODE_BACKEND_URL') || 'https://placeholder-qa-node',
        wsUrl: requireProdValue(process.env.REACT_APP_WS_URL, 'REACT_APP_WS_URL') || 'wss://placeholder-qa-ws',
    },
    production: {
        javaBackend: requireProdValue(process.env.REACT_APP_JAVA_BACKEND_URL, 'REACT_APP_JAVA_BACKEND_URL'),
        userService: requireProdValue(process.env.REACT_APP_USER_SERVICE_URL, 'REACT_APP_USER_SERVICE_URL'),
        nodeBackend: requireProdValue(process.env.REACT_APP_NODE_BACKEND_URL, 'REACT_APP_NODE_BACKEND_URL'),
        wsUrl: requireProdValue(process.env.REACT_APP_WS_URL, 'REACT_APP_WS_URL'),
    },
};

// Auth0 config - use direct process.env access for CRA replacement at build time
const auth0Domain = IS_PRODUCTION
    ? requireProdValue(process.env.REACT_APP_AUTH0_DOMAIN, 'REACT_APP_AUTH0_DOMAIN')
    : (process.env.REACT_APP_AUTH0_DOMAIN || 'localhost');
const auth0ClientId = IS_PRODUCTION
    ? requireProdValue(process.env.REACT_APP_AUTH0_CLIENT_ID, 'REACT_APP_AUTH0_CLIENT_ID')
    : (process.env.REACT_APP_AUTH0_CLIENT_ID || 'local-client-id');
const auth0Audience = IS_PRODUCTION
    ? requireProdValue(process.env.REACT_APP_AUTH0_AUDIENCE, 'REACT_APP_AUTH0_AUDIENCE')
    : (process.env.REACT_APP_AUTH0_AUDIENCE || 'https://localhost/');

const config = {
    environment: ENVIRONMENT,
    apiUrls: API_CONFIGS[ENVIRONMENT] || API_CONFIGS.local,
    auth0: {
        domain: auth0Domain,
        clientId: auth0ClientId,
        callbackPath: process.env.REACT_APP_AUTH0_CALLBACK_PATH || '/auth/callback',
        audience: auth0Audience
    }
};

export default config;

export const getApiUrl = (service) => {
    const url = config.apiUrls[service];
    if (!url) {
        throw new Error(`No URL configured for service: ${service}`);
    }
    return url;
};

export const getWebSocketUrl = () => {
    return config.apiUrls.wsUrl || 'wss://api.radix.trading';
};

// Helper function to get full callback URL for Auth0
export const getCallbackUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}${config.auth0.callbackPath}`;
};

// Helper function to check if we're in development
export const isDevelopment = () => {
    return ENVIRONMENT === 'local' || ENVIRONMENT === 'dev';
};

// Helper function to get the base backend URL
export const getBackendBaseUrl = () => {
    return getApiUrl('javaBackend');
};

// API endpoint helpers - Current and Future endpoints
export const API_ENDPOINTS = {
    // Trading (Current)
    SUBMIT_ORDER: `${getApiUrl('javaBackend')}/api/trade`,
    GET_TRADE_HISTORY: `${getApiUrl('javaBackend')}/api/trade-history`,

    // Trading (Future)
    GET_ORDERS: `${getApiUrl('javaBackend')}/api/orders`,
    CANCEL_ORDER: (orderId) => `${getApiUrl('javaBackend')}/api/orders/${orderId}`,
    GET_TRADES: `${getApiUrl('javaBackend')}/api/v1/trades`,
    GET_USER_TRADES: `${getApiUrl('javaBackend')}/api/v1/users/trades`,

    // Market Data (Future)
    GET_ORDER_BOOK: (symbol) => `${getApiUrl('javaBackend')}/api/v1/orderbook/${symbol}`,
    GET_MARKET_DATA: `${getApiUrl('javaBackend')}/api/v1/market-data`,

    // User (Current)
    GET_USER_PROFILE: `${getApiUrl('javaBackend')}/api/user/profile`,
    UPDATE_USER_PROFILE: `${getApiUrl('javaBackend')}/api/user/profile`,

    // Positions (Future)
    GET_POSITIONS: `${getApiUrl('javaBackend')}/api/v1/positions`,

    // Health & Test endpoints (Current)
    HEALTH_CHECK: `${getApiUrl('javaBackend')}/api/health`,
    TEST_ENDPOINT: `${getApiUrl('javaBackend')}/api/test`,

    // Actuator endpoints (Current)
    ACTUATOR_HEALTH: `${getApiUrl('javaBackend')}/api/actuator/health`,
    ACTUATOR_INFO: `${getApiUrl('javaBackend')}/api/actuator/info`,

    // WebSocket endpoints (Current)
    WS_TRADE_UPDATES: `${getWebSocketUrl()}/trade-updates`,
    WS_TRADES: `${getWebSocketUrl()}/ws/trades`,

    // WebSocket endpoints (Future)
    WS_ORDERS: `${getWebSocketUrl()}/ws/orders`,
    WS_MARKET_DATA: `${getWebSocketUrl()}/ws/market-data`,
};
